import { Link, useLocation } from "react-router-dom";
import { Menu, X, Flame, Mail, MapPin, Phone } from "lucide-react";
import { useState, type ReactNode } from "react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/curriculum", label: "Curriculum" },
  { to: "/structure", label: "Structure" },
  { to: "/admissions", label: "Admissions" },
  { to: "/apply", label: "Apply" },
  { to: "/register", label: "Register" },
  { to: "/contact", label: "Contact" },
] as const;

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group">
      <span className="grid h-10 w-10 place-items-center rounded-xl bg-[image:var(--gradient-primary)] text-primary-foreground shadow-[var(--shadow-elegant)] transition-transform group-hover:rotate-6">
        <Flame className="h-5 w-5" strokeWidth={2.5} />
      </span>
      <span className="flex flex-col leading-tight">
        <span className="font-display text-base font-bold tracking-tight text-foreground">RevLinks</span>
        <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Training Institute</span>
      </span>
    </Link>
  );
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const pathname = useLocation().pathname;

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="container-x grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 py-4 lg:grid-cols-[auto_1fr_auto]">
        <Logo />
        <nav className="hidden lg:flex items-center justify-center gap-1">
          {NAV.map((item) => {
            const active = pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`relative px-4 py-2 text-sm font-medium transition-colors ${
                  active ? "text-primary" : "text-foreground/70 hover:text-foreground"
                }`}
              >
                {item.label}
                {active && (
                  <span className="absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full bg-[image:var(--gradient-primary)]" />
                )}
              </Link>
            );
          })}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/apply" className="btn-primary text-sm">Apply Now</Link>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-grid h-10 w-10 place-items-center rounded-lg border border-border"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-border bg-background animate-fade-in">
          <nav className="container-x flex flex-col py-4">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={`py-3 text-base font-medium ${
                  pathname === item.to ? "text-primary" : "text-foreground/80"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link to="/apply" onClick={() => setOpen(false)} className="btn-primary mt-3 self-start text-sm">
              Apply Now
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="mt-24 border-t border-border bg-[color:var(--cream)]">
      <div className="container-x grid gap-10 py-16 md:grid-cols-4">
        <div className="md:col-span-1">
          <Logo />
          <p className="mt-4 max-w-sm text-sm text-muted-foreground">
            A Revocubelinks Group initiative — building Nigeria's next generation of solar,
            energy and e-mobility professionals through hands-on, industry-grade training.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-foreground">Explore</h4>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
            {NAV.slice(1).map((n) => (
              <li key={n.to}><Link to={n.to} className="hover:text-primary">{n.label}</Link></li>
            ))}
            <li><Link to="/admin/login" className="hover:text-primary">Admin login</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-foreground">Reach us</h4>
          <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
            <li className="flex gap-2"><MapPin className="mt-0.5 h-4 w-4 text-primary shrink-0" /> 130b Oba Ladejobi, Ikeja GRA, Lagos</li>
            <li className="flex gap-2"><Mail className="mt-0.5 h-4 w-4 text-primary shrink-0" /> Institute@revocubelinks.com</li>
            <li className="flex gap-2"><Phone className="mt-0.5 h-4 w-4 text-primary shrink-0" /> +234 07053944594</li>
            <li className="flex gap-2"><Phone className="mt-0.5 h-4 w-4 text-primary shrink-0" /> +234 09168629627 (WhatsApp)</li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-foreground">Legal</h4>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
            <li><Link to="/terms" className="hover:text-primary">Terms and Conditions</Link></li>
            <li><Link to="/privacy" className="hover:text-primary">Privacy Policy</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="container-x flex flex-col items-center justify-between gap-2 py-6 text-xs text-muted-foreground md:flex-row">
          <p>© {new Date().getFullYear()} RevLinks Integrated Services Training Institute. All rights reserved.</p>
          <p>A Revocubelinks Group initiative.</p>
        </div>
      </div>
    </footer>
  );
}

export function PageShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">{children}</main>
      <SiteFooter />
    </div>
  );
}

export function PageHero({
  eyebrow,
  title,
  intro,
}: {
  eyebrow: string;
  title: ReactNode;
  intro?: string;
}) {
  return (
    <section className="relative overflow-hidden border-b border-border bg-[image:var(--gradient-radial)]">
      <div className="container-x py-20 md:py-28">
        <p className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-ring" />
          {eyebrow}
        </p>
        <h1 className="animate-fade-up delay-100 mt-5 max-w-3xl text-4xl font-bold leading-[1.05] text-foreground md:text-6xl">
          {title}
        </h1>
        {intro && (
          <p className="animate-fade-up delay-200 mt-6 max-w-2xl text-lg text-muted-foreground">
            {intro}
          </p>
        )}
      </div>
    </section>
  );
}
